import { useState } from 'react'
import { Link } from 'react-router-dom'
import { GiftIcon } from '@heroicons/react/24/outline'
import { formatPriceSimple } from '@/utils/courseFormat'
import { useAuthStore } from '@/store/authStore'
import { shareKmCombo } from '@/utils/shareKmCombo'

export type GiftOfferAsSource = {
  giftCourseId: number
  giftCourseName: string
  giftCourseCode: string
  giftCourseHashId: string | null
  giftCourseSlug: string | null
  giftCourseFee?: number
  sourceCourseFee?: number
}

export type GiftOfferAsGift = {
  sourceCourseId: number
  sourceCourseName: string
  sourceCourseCode: string
  sourceCourseHashId: string | null
  sourceCourseSlug: string | null
  sourceCourseFee?: number
  giftCourseFee?: number
}

type Props = {
  asSource?: GiftOfferAsSource[]
  asGift?: GiftOfferAsGift[]
  className?: string
  /** Trang khóa chính: CTA mở form đăng ký */
  onRegisterCombo?: () => void
  /** Giá khóa gốc đã hiện ở purchase card phía trên — ẩn khối fee trùng (chỉ asSource) */
  hideSourceFee?: boolean
  /** Tên khóa đang xem (khóa được tặng) — dùng trong card asGift */
  currentCourseName?: string
  /** Học viên đã đăng ký khóa chính — CTA giới thiệu (share) */
  isSourceEnrolled?: boolean
  /** Khóa chính (để share tin nhắn KM) */
  sourceCourseName?: string
  sourceCourseHashId?: string | null
  sourceCourseId?: number
}

const coursePath = (hashId: string | null | undefined, id: number) =>
  `/courses/${hashId || id}`

/**
 * Card KM combo trên trang chi tiết khóa chính / khóa tặng.
 */
const CourseGiftPromoBanner = ({
  asSource = [],
  asGift = [],
  className = '',
  onRegisterCombo,
  hideSourceFee = false,
  currentCourseName,
  isSourceEnrolled = false,
  sourceCourseName,
  sourceCourseHashId,
  sourceCourseId,
}: Props) => {
  const user = useAuthStore((s) => s.user)
  const [sharingId, setSharingId] = useState<number | null>(null)

  if (asSource.length === 0 && asGift.length === 0) return null

  const handleShare = async (g: GiftOfferAsSource) => {
    if (sharingId != null || sourceCourseId == null) return
    setSharingId(g.giftCourseId)
    try {
      await shareKmCombo({
        sourceCourseName: sourceCourseName || currentCourseName || 'Khóa chính',
        giftCourseName: g.giftCourseName,
        giftCourseFee: g.giftCourseFee ?? 0,
        sourceCourseHashId,
        sourceCourseId,
        refUserId: user?.id ?? null,
      })
    } catch {
      // toast in helper
    } finally {
      setSharingId(null)
    }
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {asSource.map((g) => {
        const feeA = g.sourceCourseFee ?? 0
        const feeB = g.giftCourseFee ?? 0
        const total = feeA + feeB
        const isSharing = sharingId === g.giftCourseId
        return (
          <div
            key={`src-${g.giftCourseId}`}
            className="rounded-eh border border-eh-border bg-m2-surface-lowest p-5 sm:p-6 shadow-eh-soft"
          >
            {!hideSourceFee ? (
              <div className="mb-4">
                <p className="text-sm text-eh-muted">Học phí khóa học</p>
                <p className="mt-1 font-display text-2xl font-semibold tracking-tight text-eh-forest-ink">
                  {formatPriceSimple(feeA)}
                </p>
              </div>
            ) : null}

            <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-m2-error">
              <GiftIcon className="h-4 w-4" aria-hidden />
              Ưu đãi combo
            </p>

            <p className="mt-3 text-base font-semibold text-eh-charcoal sm:text-lg">Tặng kèm</p>
            <Link
              to={coursePath(g.giftCourseHashId, g.giftCourseId)}
              className="mt-2 block text-base font-semibold text-eh-forest-ink underline underline-offset-2 hover:opacity-80"
            >
              {g.giftCourseName}
            </Link>

            <p className="mt-2 flex flex-wrap items-baseline gap-2 text-sm">
              <span className="text-eh-muted line-through">{formatPriceSimple(feeB)}</span>
              <span className="text-lg font-bold text-m2-error">0 đ</span>
            </p>

            <div className="mt-5 border-t border-eh-border pt-4 space-y-1.5 text-sm text-eh-charcoal">
              <p>
                Giá trị nhận được{' '}
                <strong className="font-semibold">{formatPriceSimple(total)}</strong>
              </p>
              <p>
                Học phí khóa chính{' '}
                <strong className="font-semibold text-eh-forest-ink">
                  {formatPriceSimple(feeA)}
                </strong>
              </p>
            </div>

            {isSourceEnrolled ? (
              <button
                type="button"
                onClick={() => handleShare(g)}
                disabled={isSharing}
                className="eh-btn mt-5 w-full !bg-eh-keylime !text-eh-forest-ink hover:!bg-eh-sage disabled:opacity-60"
              >
                {isSharing ? (
                  'Đang chia sẻ…'
                ) : (
                  <>
                    Giới thiệu bạn học <span className="eh-arrow">→</span>
                  </>
                )}
              </button>
            ) : onRegisterCombo ? (
              <button
                type="button"
                onClick={onRegisterCombo}
                className="eh-btn mt-5 w-full !bg-eh-keylime !text-eh-forest-ink hover:!bg-eh-sage"
              >
                Đăng ký Combo <span className="eh-arrow">→</span>
              </button>
            ) : (
              <Link
                to="#hoc-phi"
                className="eh-btn mt-5 inline-flex w-full justify-center !bg-eh-keylime !text-eh-forest-ink hover:!bg-eh-sage"
              >
                Đăng ký Combo <span className="eh-arrow">→</span>
              </Link>
            )}
          </div>
        )
      })}

      {asGift.map((s) => {
        const feeA = s.sourceCourseFee ?? 0
        const feeB = s.giftCourseFee ?? 0
        const total = feeA + feeB
        const href = coursePath(s.sourceCourseHashId, s.sourceCourseId)
        const giftName = currentCourseName?.trim() || 'Khóa này'
        return (
          <div
            key={`gift-${s.sourceCourseId}`}
            className="rounded-eh border border-eh-border bg-m2-surface-lowest p-5 sm:p-6 shadow-eh-soft"
          >
            <p className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-m2-error">
              <GiftIcon className="h-4 w-4" aria-hidden />
              Ưu đãi combo
            </p>

            <div className="mt-5 space-y-3 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <Link
                  to={href}
                  className="min-w-0 font-semibold text-eh-forest-ink underline underline-offset-2 hover:opacity-80"
                >
                  {s.sourceCourseName}
                </Link>
                <span className="shrink-0 text-right font-display text-base font-semibold text-eh-forest-ink tabular-nums">
                  {formatPriceSimple(feeA)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="min-w-0 font-semibold text-eh-charcoal">{giftName}</span>
                <span className="shrink-0 text-right font-display text-base font-semibold text-eh-forest-ink tabular-nums">
                  {formatPriceSimple(feeB)}
                </span>
              </div>
            </div>

            <div className="mt-5 space-y-2 border-t border-eh-border pt-4 text-sm">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-eh-muted">Tổng giá trị 2 khóa</span>
                <span className="shrink-0 text-right font-display text-base text-eh-muted line-through tabular-nums">
                  {formatPriceSimple(total)}
                </span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-medium text-eh-charcoal">Giá combo</span>
                <span className="shrink-0 text-right font-display text-lg font-semibold text-eh-forest-ink tabular-nums">
                  {formatPriceSimple(feeA)}
                </span>
              </div>
            </div>

            <Link
              to={href}
              className="eh-btn mt-5 inline-flex w-full justify-center !bg-eh-keylime !text-eh-forest-ink hover:!bg-eh-sage"
            >
              Đăng ký Combo <span className="eh-arrow">→</span>
            </Link>
          </div>
        )
      })}
    </div>
  )
}

export default CourseGiftPromoBanner
